/**
 * CancelRetentionModal — one-screen retention attempt before cancel (Screen 5).
 *
 * Per the resolved decision, cancellation itself is handled in the Stripe
 * Customer Portal — this modal is a single empathetic save attempt, not a
 * funnel. It states what the user keeps vs. loses, then offers "Continue to
 * cancel" (→ Stripe Customer Portal via `useCustomerPortal`, where the actual
 * cancel happens) and "Never mind" to dismiss.
 *
 * Reuses the shared `ui/Modal.js` primitive (focus trap, ESC close, body scroll
 * lock, `role="dialog"` + `aria-modal`, React portal). Per design Screen 5 the
 * primary safe action ("Never mind") is the default focus; the destructive
 * "Continue to cancel" requires a deliberate Tab.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is shown.
 * @param {() => void} props.onClose - Dismiss handler ("Never mind" / ESC / ×).
 * @param {string} [props.planName='paid'] - Plan name for the heading/copy.
 * @param {string} [props.periodEndIso] - ISO date the access ends (current
 *   period end); shown as "keep access until {date}".
 */

import React, { useRef } from 'react';
import Modal from '../ui/Modal';
import { useCustomerPortal } from '../../hooks/useCustomerPortal';
import styles from './CancelRetentionModal.module.css';

/** What the user loses when dropping to Explorer (design Screen 5). */
const LOSE_ITEMS = [
  'Purchase agreement generation',
  'AI negotiation help',
  'Document e-signing',
];

/** What the user keeps on the free Explorer plan (design Screen 5). */
const KEEP_ITEMS = [
  'All your saved properties and offers',
  'The ability to browse and tour',
];

/** Format an ISO date as e.g. "Jun 30, 2026"; '' if unparseable. */
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CancelRetentionModal({
  open,
  onClose,
  planName = 'paid',
  periodEndIso,
}) {
  const keepRef = useRef(null);
  const { openPortal, loading, error } = useCustomerPortal();
  const periodEnd = formatDate(periodEndIso);

  const handleContinue = () => {
    // Stripe Customer Portal is where the actual cancel happens.
    openPortal();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cancel your ${planName} plan?`}
      size="sm"
      initialFocusRef={keepRef}
      footer={
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={onClose}
            ref={keepRef}
            data-testid="cancel-dismiss"
          >
            Never mind
          </button>
          <button
            type="button"
            className={styles.danger}
            onClick={handleContinue}
            disabled={loading}
            aria-busy={loading}
            data-testid="cancel-continue"
          >
            {loading ? 'Opening Stripe…' : 'Continue to cancel'}
          </button>
        </div>
      }
    >
      <div data-testid="cancel-retention-modal">
        <p className={styles.intro}>
          Before you go — if you cancel today
          {periodEnd ? `, you'll keep access until ${periodEnd}, then` : ', you\'ll'}{' '}
          drop to the free Explorer plan.
        </p>

        <p className={styles.groupLabel}>You'll lose:</p>
        <ul className={styles.list}>
          {LOSE_ITEMS.map((item) => (
            <li key={item} className={styles.loseItem}>{item}</li>
          ))}
        </ul>

        <p className={styles.groupLabel}>You'll keep:</p>
        <ul className={styles.list}>
          {KEEP_ITEMS.map((item) => (
            <li key={item} className={styles.keepItem}>{item}</li>
          ))}
        </ul>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
